import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { InfluenceCategory, InfluenceScore, getInfluenceCategories, getInfluenceScores } from '../../services/influencesService';
import { IconButton, Input, Spinner, Swatch, isHexColor, readableOn } from '../ui';

/*
 * Custom-drawn range: a thin track filled in the area colour and a white-ringed thumb.
 * (Native `accent-color` darkens the unfilled track for light hues, so it is not used.)
 */
const RANGE = [
  'h-6 cursor-pointer appearance-none rounded-full bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40',
  '[&::-webkit-slider-runnable-track]:h-1 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:[background:var(--range-track)]',
  '[&::-webkit-slider-thumb]:-mt-1.5 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-[var(--range-color)] [&::-webkit-slider-thumb]:shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.2)]',
  '[&::-moz-range-track]:h-1 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-neutral-200',
  '[&::-moz-range-progress]:h-1 [&::-moz-range-progress]:rounded-full [&::-moz-range-progress]:bg-[var(--range-color)]',
  '[&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-[var(--range-color)] [&::-moz-range-thumb]:shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.2)]',
].join(' ');

const INK = '#0a0a0a';
const TRACK = '#e5e5e5';

type Props = {
  entityType: 'stakeholder' | 'organization';
  entityId?: string;
  onChange?: (scores: { categoryId: string; score: number }[]) => void;
  /** Optional small heading above the rows; pages usually title the section themselves */
  title?: React.ReactNode;
};

export const InfluenceScoresEditor: React.FC<Props> = ({ entityType, entityId, onChange, title }) => {
  const [categories, setCategories] = useState<InfluenceCategory[]>([]);
  const [values, setValues] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const onChangeRef = useRef<Props['onChange']>(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cats = await getInfluenceCategories();
        if (!mounted) return;
        setCategories(cats);
      } catch {}
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!entityId) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const scores: InfluenceScore[] = await getInfluenceScores(entityType, entityId);
        if (!mounted) return;
        const map: Record<string, number> = {};
        scores.forEach(s => { map[s.categoryId] = s.score; });
        setValues(map);
        onChangeRef.current?.(Object.entries(map).map(([categoryId, score]) => ({ categoryId, score })));
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [entityId, entityType]);

  const update = (categoryId: string, score: number | '') => {
    setValues(prev => {
      const next = { ...prev } as Record<string, number>;
      if (score === '') delete next[categoryId];
      else next[categoryId] = score as number;
      onChangeRef.current?.(Object.entries(next).map(([cid, s]) => ({ categoryId: cid, score: s })));
      return next;
    });
  };

  return (
    <div className="font-ui">
      {(title || loading) && (
        <div className="mb-1 flex h-6 items-center justify-between gap-3">
          {title ? <p className="text-[12px] text-neutral-500">{title}</p> : <span />}
          {loading && (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-neutral-400">
              <Spinner className="h-3 w-3" />
              Loading
            </span>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
        {categories.map(c => {
          const val = values[c.id];
          const set = typeof val === 'number';
          // The area's taxonomy colour drives the swatch, slider and value; ink when none is set
          const color = isHexColor(c.color) ? c.color : undefined;
          return (
            <div
              key={c.id}
              className="flex flex-wrap items-center gap-x-2 border-b border-neutral-200/70 py-2 last:border-b-0 sm:flex-nowrap sm:gap-x-3 sm:py-1.5 sm:[&:nth-last-child(2):nth-child(odd)]:border-b-0"
            >
              <label
                htmlFor={`influence-${c.id}`}
                className="order-1 flex min-w-0 flex-1 items-center gap-2 text-[13px] text-ink sm:w-32 sm:flex-none"
                title={c.description || c.label}
              >
                <Swatch color={color} />
                <span className="truncate">{c.label}</span>
              </label>
              <input
                id={`influence-${c.id}`}
                type="range"
                min={0}
                max={100}
                value={set ? val : 0}
                onChange={e => update(c.id, Number(e.target.value))}
                className={`${RANGE} order-4 mt-1 w-full sm:order-2 sm:mt-0 sm:w-auto sm:min-w-0 sm:flex-1 ${set ? '' : 'opacity-40'}`}
                style={
                  {
                    '--range-color': color || INK,
                    '--range-track': `linear-gradient(to right, ${color || INK} ${set ? val : 0}%, ${TRACK} ${set ? val : 0}%)`,
                  } as React.CSSProperties
                }
              />
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={100}
                value={set ? val : ''}
                placeholder="—"
                aria-label={`${c.label} score`}
                onChange={e => {
                  const raw = e.target.value;
                  update(c.id, raw === '' ? '' : Math.max(0, Math.min(100, Math.round(Number(raw)))));
                }}
                className="order-2 flex-shrink-0 tabular-nums [appearance:textfield] sm:order-3 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                style={{
                  width: 56,
                  height: 32,
                  paddingLeft: 8,
                  paddingRight: 8,
                  textAlign: 'right',
                  ...(color && set ? { color: readableOn(color) } : {}),
                }}
              />
              <IconButton
                label="Clear"
                size="xs"
                onClick={() => update(c.id, '')}
                disabled={!set}
                className={`order-3 sm:order-4 ${set ? '' : 'invisible'}`}
              >
                <X className="h-3.5 w-3.5" strokeWidth={1.75} />
              </IconButton>
            </div>
          );
        })}
      </div>
    </div>
  );
};

